/* eslint-disable react-hooks/exhaustive-deps */
import { FC, memo, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IColumn } from 'types'
import { useGET_plopQuestionCategoriesQuery, usePUT_plopReportSendParentsMutation, usePUT_plopReportSendStudentMutation } from 'redux/api/plopApi'
import { formatDateTime, translateGradeLevel } from 'utils/helpers'
import { Button } from '@mui/material'
import { actionIcon, eyeIcon, PRIMARY_GREEN } from 'utils/customizedTheme'
import { FaEye } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import PopupMenu from 'components/molecules/PopupMenu'
import { CheckCircle, ForwardToInbox, MoreVert, Send } from '@mui/icons-material'

export const useTableModel = () => {
  const { t } = useTranslation()

  const handleActionsFormat = useCallback(
    (cellValue: string, row: any, index: number) => <Actions row={row} index={index} />,
    [],
  )

  const handlePercentageFormat = useCallback(
    (cellValue: string, row: any, index: number) => `${cellValue || "0"}%`,
    [],
  )
  
  const handleDateFormat = useCallback(
    (cellValue: string) => (cellValue ? formatDateTime(cellValue) : ''),
    [],
  )
  
  const handleGradeLevelFormat = useCallback(
    (cellValue: string, row: any) => translateGradeLevel(cellValue, true),
    [],
  )

  const { data: plopQuestionCategories } = useGET_plopQuestionCategoriesQuery()
  
  let columns: IColumn[] = [
    {
      title: `${t('common.date')}`,
      field: 'created_at',
      order: false,
      width: '10%',
      format: handleDateFormat
    },
    {
      title: `${t('common.student')}`,
      field: 'student',
      order: false,
      width: '15%',
    },
    {
      title: `${t('common.grade_level')}`,
      field: 'grade_level',
      order: true,
      width: '10%',
      format: handleGradeLevelFormat
    }
  ]
  
  if(plopQuestionCategories)
    for(let c of plopQuestionCategories.list){
      columns.push({
        title: `${c.title}`,
        field: '',
        nested_field: 'category_scores',
        nested_field_id: c.id,
        nested_field_value: 'score',
        order: false,
        width: '15%',
      })
    }

  columns.push({
      title: `${t('common.overall-performance')}`,
      field: 'baseline_score',
      width: '10%',
      order: true,
      format: handlePercentageFormat,
  })

  columns.push({
    title: '',
    field: '',
    width: '10%',
    align: 'end',
    order: false,
    format: handleActionsFormat,
  })

  return columns
}

interface IProps {
  row: any
  index: number
}

const Actions: FC<IProps> = memo(({ row, index }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuAnchor, setMenuAnchor] = useState(anchorRef.current)
  
  const [
    sendStudent,
  ] = usePUT_plopReportSendStudentMutation()

  const [
      sendParents,
    ] = usePUT_plopReportSendParentsMutation()

  return (
    <>
      <div ref={anchorRef}>
        <Button
            onClick={() => setMenuAnchor(anchorRef.current)}
            variant='outlined'
            color='black'
            disableElevation
            sx={{ ...actionIcon  }}
          >
            <MoreVert />
        </Button>

        <PopupMenu
          id={`plop-report-menu-${row.id}`}
          anchorEl={menuAnchor}
          setIsMenuOpen={setMenuAnchor}
          items={[
            {
              icon: <FaEye fontSize={'22px'}/>,
              text: `${t('common.view-report')}`,
              onClick: () => navigate(`/plop-reports/view/${row.id}`)
            },
            {
              icon: row.is_sent_to_student ? <CheckCircle sx={{color: PRIMARY_GREEN}} />: <Send />,
              text: `${t('common.send-to-student')}`,
              onClick: row.is_sent_to_student ? () => {} : () => sendStudent(row.id)
            },
            {
              icon: row.is_sent_to_parents ? <CheckCircle sx={{color: PRIMARY_GREEN}} />: <ForwardToInbox />,
              text: `${t('common.send-to-parents')}`,
              onClick: row.is_sent_to_parents ? () => {} : () => sendParents(row.id)
            },
          ]}
        />
      </div>
    </>
  )
})